import BlogHeader from '@/components/BlogHeader'
import CaseStudyFeed from '@/components/CaseStudyFeed'
import CaseStudySearchForm from '@/components/CaseStudySearchForm'
import Main from '@/components/Main'
import SEO from '@/components/SEO'
import { FeaturedImageFragment } from '@/fragments/FeaturedImage'
import styles from '@/styles/pages/_default.module.scss'
import { gql } from '@apollo/client'
import { useEffect, useState } from 'react'

export default function WorkPage(props) {
  const [posts, setPosts] = useState(props?.data?.caseStudies?.nodes || [])
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [currentCategory, setCurrentCategory] = useState('')

  const metaDesc = props?.data?.page?.seo?.metaDesc
  const { title, featuredImage } = props?.data?.page ?? { title: '' }
  const categories = props?.data?.caseStudyServices
  const seoTitle = props?.data?.page?.seo?.title

  // Filter out categories that have no associated case studies
  const filteredCategories = categories?.nodes?.filter((category) =>
    props?.data?.caseStudies?.nodes?.some((post) =>
      post?.caseStudyFields?.casestudyServices?.nodes?.some((service) => service?.name === category.name)
    )
  )

  const handleSearch = (searchTerm, selectedCategory) => {
    // Save search state with unique keys for work page
    sessionStorage.setItem('workSearchTerm', searchTerm)
    sessionStorage.setItem('workSearchCategory', selectedCategory)
    setCurrentSearchTerm(searchTerm)
    setCurrentCategory(selectedCategory)

    let filteredPosts = props?.data?.caseStudies?.nodes
    const normalizedSearchTerm = searchTerm?.trim().toLowerCase()

    if (normalizedSearchTerm) {
      filteredPosts = filteredPosts.filter((post) => {
        const titleMatches = post.title && post.title.toLowerCase().includes(normalizedSearchTerm)
        const caseStudyTitleMatches =
          post?.caseStudyFields?.title && post.caseStudyFields.title.toLowerCase().includes(normalizedSearchTerm)
        return titleMatches || caseStudyTitleMatches
      })
    }

    if (selectedCategory && selectedCategory !== 'All Categories') {
      filteredPosts = filteredPosts.filter((post) =>
        post?.caseStudyFields?.casestudyServices?.nodes?.some((service) => service?.slug === selectedCategory)
      )
    }
    setPosts(filteredPosts)
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const categoryFromURL = urlParams.get('category')
    const savedSearchTerm = sessionStorage.getItem('workSearchTerm')
    const savedCategory = sessionStorage.getItem('workSearchCategory')

    const categoryToUse = categoryFromURL || savedCategory

    if (savedSearchTerm || categoryToUse) {
      handleSearch(savedSearchTerm || '', categoryToUse || '')
      setCurrentSearchTerm(savedSearchTerm || '')
      setCurrentCategory(categoryToUse || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <SEO title={seoTitle || title} description={metaDesc} imageUrl={featuredImage?.node?.sourceUrl} />
      <BlogHeader pageFields={props?.data?.page} image={featuredImage?.node} disableOverlay={true} />

      <Main>
        <div className={`container ${styles.workContainer}`}>
          <CaseStudySearchForm
            categories={{ nodes: filteredCategories }}
            onSearch={handleSearch}
            initialSearchTerm={currentSearchTerm}
            initialCategory={currentCategory}
          />
          <div className={styles.blogFeed}>
            <CaseStudyFeed caseStudies={posts} id="case-studies-list" />
          </div>
        </div>
      </Main>
    </>
  )
}

WorkPage.query = gql`
  ${FeaturedImageFragment}
  query GetPageData($databaseId: ID!, $asPreview: Boolean = false) {
    page(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      title
      content
      seo {
        metaDesc
        title
      }
      ...FeaturedImageFragment
      innerHeroFields {
        content
        eyebrowText
        fieldGroupName
        includeForm
        title
      }
    }
    caseStudyServices(first: 50) {
      nodes {
        name
        uri
        slug
      }
    }
    caseStudies(first: 100) {
      nodes {
        title
        uri
        caseStudyFields {
          casestudyServices {
            nodes {
              ... on CaseStudyService {
                id
                name
                slug
              }
            }
          }
          heroImage {
            node {
              altText
              sourceUrl
            }
          }
          cardImage {
            node {
              altText
              sourceUrl
            }
          }
          title
        }
      }
    }
  }
`

WorkPage.variables = ({ databaseId }, ctx) => {
  return {
    databaseId,
    asPreview: ctx?.asPreview
  }
}
